/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Navigation } from 'swiper';
import { useSize } from 'ahooks';
import { getUserBookmarks } from '../../actions/bookmarks/bookmarks.actions';
import {
  selectVCardContentSerieState,
  selectVCardBookmarksSeriesState,
} from '../../selectors/vcard.selector';
import Episode from './Episode';
import MobileSelectSeasons from '../MobileSelectSeasons';
import { useNavigate } from 'react-router-dom';
import { getRegion } from 'utils/Region';
import getContentAvailability from 'utils/getContentAvailability';
import Metrics from 'services/metrics';

import './EpisodesSection.scss';

const EVENT_NAME = 'select_content';
const CURRENT_SECTION = 'vcard';

const renderSkeletons = n =>
  [...Array(n)].map(e => <div className="episodes_skeleton" key={e} />);


const handleEpisodeClick = (clickedEpisode, navigate) => {
  const episodeUri = clickedEpisode?.title_uri;
  const episodeId = clickedEpisode?.group_id || clickedEpisode?.id || '';

  const contentAvailability = getContentAvailability(
    clickedEpisode?.format_types?.split(','),
    clickedEpisode?.proveedor_code,
  )?.toString();

  const eventInfo = {
    event: EVENT_NAME,
    content_section: CURRENT_SECTION,
    content_list: clickedEpisode?.seasonName,
    content_id: clickedEpisode?.id,
    content_name: clickedEpisode?.title,
    content_type: '', // No es posible obtener este parametro.
    content_category: '', // No es posible obtener este parametro.
    content_availability: contentAvailability,
    content_position: clickedEpisode?.contentPosition,
  };

  Metrics.newEvent(eventInfo);

  if (!episodeUri || !episodeId) return;

  navigate({
    pathname: `/${getRegion()}/vcard/homeuser/${episodeUri}/${episodeId}`,
    state: { eventInfo },
  });
}

const renderEpisodes = (season, userHasSession, bookmarks, size, navigate) => {
  if (!season?.episodes) return null;

  const seasonName = `temporada ${season?.number}`;

  return season.episodes.map((episode, i) => {
    let episodeBookmark = null;

    if (userHasSession) {
      episodeBookmark = bookmarks.find(b => b.id === episode.id);
    }

    const clickedEpisode = {...episode, contentPosition: i, seasonName};

    if (size.width <= 650) {
      return (
        <Episode
          key={episode.id}
          data={episode}
          bookmark={episodeBookmark}
          showBookmark={userHasSession}
          size={size}
          handleClick={() => handleEpisodeClick(clickedEpisode, navigate)}
        />
      );
    }

    return (
      <SwiperSlide key={episode.id}>
        <Episode
          data={episode}
          bookmark={episodeBookmark}
          showBookmark={userHasSession}
          size={size}
          handleClick={() => handleEpisodeClick(clickedEpisode, navigate)}
        />
      </SwiperSlide>
    );
  });
};

SwiperCore.use([Pagination, Navigation]);

const EpisodesSection = ({ groupId, episode, title }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dom = document.querySelector('body');
  const size = useSize(dom);
  const seasonNumber = episode.season ? Number(episode.season) : 1;
  const [seasonSelected, changeSeason] = useState(seasonNumber);
  const { infoSerie, isLoadingInfoSerie } = useSelector(
    selectVCardContentSerieState,
  );
  const { groups, currentSeasonId, isBookmarkLoading } = useSelector(
    selectVCardBookmarksSeriesState,
  );
  const userHasSession = useSelector(state => state.user.userhash);
  const translations = useSelector(state => state.translations);
  const chevronLeftIcon =
    useSelector(
      state => state.asset?.playing_playerControls_toolbar_chevron_left,
    ) || '';
  const chevronRightIcon =
    useSelector(
      state => state.asset?.playing_playerControls_toolbar_chevron_right,
    ) || '';

  const seasonsList = infoSerie.seasons || [];
  const season = seasonsList[seasonSelected - 1] || seasonsList[0] || {};

  useEffect(
    () => {
      if (!season.id || !userHasSession) return;
      if (currentSeasonId !== season.id && !isBookmarkLoading) {
        const groupIds = season.episodes.map(e => e.id);
        dispatch(getUserBookmarks(groupIds, 'menuSeriesType', season.id));
      }
    },
    [season.id],
  );

  const options = seasonsList.map(e => ({
    id: Number(e.number),
    value: `${translations.vcard_access_abbreviationSeason_label} ${e.number}`,
    label: `${translations.vcard_access_abbreviationSeason_label} ${e.number}`,
  }));

  const generateSeasonTabs = () => {
    if (size.width <= 650) {
      return (
        <MobileSelectSeasons
          defaultSelect={`${
            translations.vcard_access_abbreviationSeason_label
          } ${seasonSelected}`}
          title={title}
          options={options}
          changeSeason={changeSeason}
        />
      );
    }

    return (
      <Swiper
        className="season-tabs"
        slidesPerView="auto"
        spaceBetween={19}
        navigation
        watchOverflow
        autoHeight
      >
        {options.map(opt => (
          <SwiperSlide key={opt.id}>
            <div
              className={`react-tabs__tab ${opt.id === seasonSelected &&
                'react-tabs__tab--selected'}`}
              onClick={() => changeSeason(opt.id)}
            >
              {opt.label}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  if (isLoadingInfoSerie) {
    return <div className="skeleton-wrapper">{renderSkeletons(5)}</div>;
  }

  return (
    <>
      {generateSeasonTabs()}
      {
        <div className="episodes_section">
          <div
            className="episodes_section__container_vcard_left_icon"
            style={{
              position: 'absolute',
              backgroundColor: 'rgba(0,0,0,0.6)',
              top: '0',
              zIndex: 100,
              width: '48px',
              height: '132px',
              cursor: 'pointer',
            }}
          >
            <img
              style={{
                marginTop: '49px',
                width: '24px',
                height: '24px',
                marginLeft: '12px',
              }}
              src={chevronLeftIcon}
            />
          </div>

          <div
            className="episodes_section__container_vcard_right_icon"
            style={{
              position: 'absolute',
              backgroundColor: 'rgba(0,0,0,0.6)',
              top: '0',
              zIndex: 100,
              width: '48px',
              height: '132px',
              right: '0',
              cursor: 'pointer',
            }}
          >
            <img
              style={{
                marginTop: '49px',
                width: '24px',
                height: '24px',
                marginLeft: '12px',
              }}
              src={chevronRightIcon}
            />
          </div>

          <Swiper
            className="episodes_carousel"
            slidesPerView="auto"
            allowTouchMove={false}
            spaceBetween={24}
            navigation={{
              prevEl: '.episodes_section__container_vcard_left_icon',
              nextEl: '.episodes_section__container_vcard_right_icon',
            }}
            watchOverflow
          >
            {renderEpisodes(
              seasonsList[
                seasonsList?.findIndex(
                  seasonItem => seasonItem.number === seasonSelected.toString(),
                )
              ],
              userHasSession,
              groups,
              size,
              navigate,
            )}
          </Swiper>
        </div>
      }
    </>
  );
};

EpisodesSection.propTypes = {
  groupId: PropTypes.string.isRequired,
  episode: PropTypes.shape({
    season: PropTypes.string,
  }),
};

EpisodesSection.defaultProps = {
  episode: {},
};

export default EpisodesSection;
